import { Actor } from "./Actor";
import { DocumentationAreaId } from "./DocumentationAreaId";
import { DocumentationAreaTypes } from "src/contexts/documentationArea/domain/DocumentationAreaTypes";
import { AvailableActor } from "./AvailableActor";
import { ActorInvitation } from "./ActorInvitation";

export type AvailableActorTemplate = {
  id: string;
  name: string;
};

export type DocumentationAreaParams = {
  id: DocumentationAreaId;
  type: DocumentationAreaTypes;
  actor?: Actor;
  actors?: Actor[];
  locale: string;
  availableActors?: AvailableActor[];
  actorInvitation?: ActorInvitation | null;
  templateName?: string | null;
  availableTemplates?: AvailableActorTemplate[];
};

export class DocumentationArea {
  private readonly id: DocumentationAreaId;
  private readonly type: DocumentationAreaTypes;
  private readonly actor: Actor;
  private readonly locale: string;
  private readonly availableActors: AvailableActor[];
  private readonly actorInvitation: ActorInvitation | null;
  private readonly templateName: string | null;
  private readonly availableTemplates: AvailableActorTemplate[];

  constructor({
    id,
    type,
    actor,
    actors,
    locale,
    availableActors = [],
    actorInvitation = null,
    templateName = null,
    availableTemplates = [],
  }: DocumentationAreaParams) {
    const selectedActor = actor ?? actors?.[0];
    if (!selectedActor) {
      throw new Error("Documentation area requires at least one actor");
    }

    this.id = id;
    this.type = type;
    this.actor = selectedActor;
    this.locale = locale;
    this.availableActors = availableActors;
    this.actorInvitation = actorInvitation ?? null;
    this.templateName = templateName;
    this.availableTemplates = availableTemplates;
  }

  public getId() {
    return this.id;
  }

  /**
   * @returns the selected actor in the current documentation area context.
   */
  public getActor() {
    return this.actor;
  }

  public getAvailableActors() {
    return this.availableActors;
  }

  public getLocale() {
    return this.locale;
  }

  public getType() {
    return this.type;
  }

  public getActorInvitation() {
    return this.actorInvitation;
  }

  public getTemplateName() {
    return this.templateName;
  }

  public getAvailableTemplates() {
    return this.availableTemplates;
  }

  public isForCompanies() {
    return this.type === DocumentationAreaTypes.COMPANY;
  }

  public isForIndividual() {
    return this.type === DocumentationAreaTypes.INDIVIDUAL;
  }

  public hasMinimumDataProvided(): boolean {
    if (this.isForCompanies()) {
      return (
        this.actor.hasIdNumber() &&
        this.actor.hasRepresentedEntityIdNumber() &&
        this.actor.hasRepresentedEntityRelationType()
      );
    }

    return this.actor.hasIdNumber();
  }
}
